import type { PoiData } from '$lib/components/pois/types';
import type { FAQEntry, LinkSegments } from '$lib/components/seo/types';
import type { AvailableLanguageTag } from '$paraglide/runtime';
import { getSegmentsLabel, segments2href } from '$lib/components/seo/linkSegments';
import { i18nPath } from '$lib/i18n/routing';
import { serializeSearchParams } from '$lib/services/urls/searchParams';
import { formatPrice } from '$lib/utils/formatPrice';
import {
	getAddressSegmentsLabels,
	getAddressSegmentsLinks,
	getHotelLinks,
	replaceHtml,
	replaceText,
} from './format';

interface FillAddressDataArgs {
	addressSegments: LinkSegments;
	question: string;
	answer: string;
	lang: AvailableLanguageTag;
}

export function fillAddressData({
	addressSegments,
	answer,
	question,
	lang,
}: FillAddressDataArgs): FAQEntry {
	const answerWithoutLinks = replaceText(
		answer,
		{
			trafficUrl: i18nPath('/map/traffic', lang),
			mapsUrl: i18nPath('/map', lang),
			hotelsUrl: segments2href(addressSegments, lang, ['map', 'ACCOMMODATION']),
			localityLabel: getSegmentsLabel(addressSegments),
			addressUrl: segments2href(addressSegments, lang),
			levelAreaLabels: getAddressSegmentsLabels(addressSegments),
		},
		false,
	);

	const answerHtml = replaceHtml(answerWithoutLinks, {
		levelAreaLinks: getAddressSegmentsLinks(addressSegments, lang),
	});

	return {
		question: replaceText(question, {
			localityLabel: getSegmentsLabel(addressSegments),
		}),
		answerHtml,
	};
}

interface FillHomeDataArgs {
	question: string;
	answer: string;
	lang: AvailableLanguageTag;
}

export function fillHomeData({ answer, question, lang }: FillHomeDataArgs): FAQEntry {
	const answerHtml = replaceText(answer, {
		trafficUrl: i18nPath('/map/traffic', lang),
		mapsUrl: i18nPath('/map', lang),
		itinerarySearchUrl: i18nPath('/routes', lang),
		restaurantsUrl: i18nPath('/map/RESTAURANT', lang),
		hotelsUrl: i18nPath('/map/ACCOMMODATION', lang),
	});

	return {
		question,
		answerHtml,
	};
}

interface FillHotelDataArgs {
	question: string;
	answer: string;
	hotelName: string;
	city: string;
	equipments: string[];
	rating: number;
	priceMin: number;
	currency: string;
}

export function fillHotelData({
	answer,
	question,
	city,
	rating,
	currency,
	priceMin,
	hotelName,
	equipments,
}: FillHotelDataArgs): FAQEntry {
	const answerHtml = replaceText(answer, {
		hotelName,
		city,
		rating: rating.toString(),
		priceMinHotel: priceMin.toString(),
		currency,
		equipments: equipments.join(', '),
	});

	return {
		question: replaceText(question, {
			hotelName,
			city,
		}),
		answerHtml,
	};
}

interface AccommodationDetail {
	type: string;
	count: number;
}

interface FillHotelsDataArgs {
	addressSegments: LinkSegments;
	totalNumberAccomodation: number;
	question: string;
	answer: string;
	lang: AvailableLanguageTag;
	accommodationDetails: AccommodationDetail[];
	popularHotels: PoiData[];
	priceMin: number;
	currency: string;
	spaCount: number;
	poolCount: number;
	familyCount: number;
	handicapCount: number;
	parkingCount: number;
}

export function fillHotelsData({
	addressSegments,
	totalNumberAccomodation,
	answer,
	question,
	lang,
	currency,
	priceMin,
	popularHotels,
	accommodationDetails,
	spaCount,
	poolCount,
	familyCount,
	handicapCount,
	parkingCount,
}: FillHotelsDataArgs): FAQEntry {
	const hotelsPath = segments2href(addressSegments, lang, ['map', 'ACCOMMODATION']);

	const spaFilterSearchParams = serializeSearchParams({
		filters: {
			ACCOMMODATION_EQUIPMENT: ['SPA'],
		},
	});

	const poolFilterSearchParams = serializeSearchParams({
		filters: {
			ACCOMMODATION_EQUIPMENT: ['POOL'],
		},
	});

	const familyFilterSearchParams = serializeSearchParams({
		filters: {
			ACCOMMODATION_EQUIPMENT: ['FAMILY_ROOM'],
		},
	});

	const handicapFilterSearchParams = serializeSearchParams({
		filters: {
			ACCOMMODATION_EQUIPMENT: ['REDUCED_MOBILITY'],
		},
	});

	const parkingFilterSearchParams = serializeSearchParams({
		filters: {
			ACCOMMODATION_EQUIPMENT: ['PARKING'],
		},
	});

	const localityLabel = getSegmentsLabel(addressSegments);

	const answerWithoutLinks = replaceText(
		answer,
		{
			localityLabel: localityLabel ?? '',
			totalNumberAccomodation: totalNumberAccomodation.toString(),
			lessCostHotel: formatPrice({
				currency,
				amount: priceMin,
				language: lang,
			}),
			spaCount: spaCount.toString(),
			poolCount: poolCount.toString(),
			parkCount: parkingCount.toString(),
			familyCount: familyCount.toString(),
			handicapCount: handicapCount.toString(),
			spaLink: `${hotelsPath}?${spaFilterSearchParams}`,
			poolLink: `${hotelsPath}?${poolFilterSearchParams}`,
			familyLink: `${hotelsPath}?${familyFilterSearchParams}`,
			parkLink: `${hotelsPath}?${parkingFilterSearchParams}`,
			handicapLink: `${hotelsPath}?${handicapFilterSearchParams}`,
			accomodationDetails: accommodationDetails
				.map(detail => `${detail.count} ${detail.type}`)
				.join(', '),
		},
		false,
	);

	const answerHtml = replaceHtml(answerWithoutLinks, {
		threeMostPopular: getHotelLinks(popularHotels, lang).join(', '),
	});

	return {
		question: replaceText(question, {
			localityLabel: localityLabel ?? '',
		}),
		answerHtml,
	};
}
